import type { EventLogStats, LogEntry } from "./eventLog";

/** Metadata carried alongside a session export, so a reader never has to guess its provenance. */
export type ExportMeta = {
  capturedAt: string;
  mode: "live" | "mock";
  appInstanceId?: string;
  sdkVersion: string;
  stats: EventLogStats;
};

/**
 * Builds a `JSON.stringify` replacer that tolerates circular payloads without mislabelling
 * shared (non-circular) references.
 *
 * Amendment A9: a naive replacer using one `WeakSet` for the whole document marks the SECOND
 * SIBLING reference to the same object as `"[Circular]"`, even though it is not a cycle — e.g.
 * two timeline entries pointing at the same SDK payload object. The fix tracks the ANCESTOR
 * PATH instead: `JSON.stringify` visits depth-first, and the replacer's `this` is always the
 * object currently holding the key being serialised. By popping the stack back down to `this`
 * before checking membership, an object is only "seen" while one of its own descendants is
 * being serialised — it is removed the moment its subtree finishes, so a later sibling or an
 * unrelated reference to the same object is serialised in full. Only a true ancestor-descendant
 * self-reference is replaced with `"[Circular]"`.
 */
function safeReplacer(): (
  this: unknown,
  key: string,
  value: unknown,
) => unknown {
  const ancestors: unknown[] = [];
  return function replacer(
    this: unknown,
    _key: string,
    value: unknown,
  ): unknown {
    if (typeof value !== "object" || value === null) return value;
    while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
      ancestors.pop();
    }
    if (ancestors.includes(value)) return "[Circular]";
    ancestors.push(value);
    return value;
  };
}

/** Serialises a whole session, meta first, for archiving or sharing. */
export function toJsonExport(
  entries: readonly LogEntry[],
  meta: ExportMeta,
): string {
  return JSON.stringify({ meta, entries }, safeReplacer(), 2);
}

/** Serialises entries one per line, for streaming into log tooling. Each line gets its own
 * replacer instance so entries never share (or leak) ancestor state. */
export function toNdjson(entries: readonly LogEntry[]): string {
  return (
    entries.map((e) => JSON.stringify(e, safeReplacer())).join("\n") + "\n"
  );
}
